<template>
  <div class="tab-wrap">
    <!-- Tab Bar -->
    <div class="tab-bar">
      <button v-if="canViewAgents" class="tab-btn" :class="{ active: activeTab === 'agentes' }" @click="activeTab = 'agentes'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="4"/>
          <path d="M20 21a8 8 0 1 0-16 0"/>
        </svg>
        Agentes
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'prioridades' }" @click="activeTab = 'prioridades'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        Prioridades
        <span class="tab-notif tab-notif-prio" :class="{ zero: prioBadge === 0 }">{{ prioBadge }}</span>
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'fora-de-horario' }" @click="activeTab = 'fora-de-horario'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
        Fora de Horário
        <span class="tab-notif tab-notif-fora" :class="{ zero: foraBadge === 0 }">{{ foraBadge }}</span>
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'aguardando-resposta' }" @click="activeTab = 'aguardando-resposta'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Aguardando
        <span class="tab-notif tab-notif-agrd" :class="{ zero: aguardandoBadge === 0 }">{{ aguardandoBadge }}</span>
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'sla' }" @click="activeTab = 'sla'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        SLA
        <span class="tab-notif tab-notif-sla" :class="{ zero: slaBadge === 0 }">{{ slaBadge }}</span>
      </button>
    </div>

    <!-- Agentes -->
    <AgentsTab
      v-if="canViewAgents"
      :conversations="visibleConversations"
      :all-agents="allAgents ?? []"
      :agent-teams-map="agentTeamsMap"
      :scope-ready="scopeReady"
      :active="activeTab === 'agentes'"
      @show-popup="onShowPopup"
      @hide-popup="emit('hidePopup')"
    />

    <!-- Prioridades -->
    <PrioridadesTab
      :conversations="visibleConversations"
      :active="activeTab === 'prioridades'"
      @update-badge="prioBadge = $event"
    />

    <!-- Fora de Horário -->
    <ForaDeHorarioTab
      :conversations="visibleConversations"
      :active="activeTab === 'fora-de-horario'"
      @update-badge="foraBadge = $event"
    />

    <!-- Aguardando Resposta -->
    <AguardandoRespostaTab
      :conversations="visibleConversations"
      :active="activeTab === 'aguardando-resposta'"
      :tolerance-minutes="toleranceMinutes"
      @update-badge="aguardandoBadge = $event"
    />

    <!-- SLA -->
    <SLATab
      :conversations="visibleConversations"
      :active="activeTab === 'sla'"
      @update-badge="slaBadge = $event"
      @show-popup="onShowPopup"
      @hide-popup="emit('hidePopup')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, toRef } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { useSLANotifications } from '@/composables/useSLANotifications'
import { usePermission } from '@/composables/usePermission'
import AgentsTab              from './AgentsTab.vue'
import PrioridadesTab         from './PrioridadesTab.vue'
import ForaDeHorarioTab       from './ForaDeHorarioTab.vue'
import AguardandoRespostaTab  from './AguardandoRespostaTab.vue'
import SLATab                 from './SLATab.vue'

const props = defineProps<{
  conversations: Conversation[]
  allAgents?: { id: number; name: string; email: string; thumbnail: string; role: string; availability_status: string }[]
  agentTeamsMap?: Record<number, number[]>
  toleranceMinutes?: number
  // Fase 4 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md) —
  // repassado pra AgentsTab, que depende de conversations/allAgents estarem prontos.
  scopeReady?: boolean
}>()

useSLANotifications(toRef(props, 'conversations'))

// Conversas com a etiqueta "avaliacao_atendimento" (fluxo interno de avaliação/CSAT do bot, ver
// AvaliacoesView) nunca devem aparecer nas abas de trabalho do dashboard — mesmo critério já usado
// em SLATab.vue e useMyConversations.ts. Filtra aqui, uma vez só, e repassa pra todas as abas
// abaixo, em vez de cada uma filtrar por conta própria (evita divergência entre abas).
const visibleConversations = computed(() =>
  props.conversations.filter(c => !c.labels.includes('avaliacao_atendimento'))
)

const emit = defineEmits<{
  showPopup: [data: PopupData, el: HTMLElement]
  hidePopup: []
}>()

const { can } = usePermission()

const canViewAgents = computed(() => can('dashboard.viewAgents'))

// First allowed default tab
const defaultTab = computed(() => canViewAgents.value ? 'agentes' : 'prioridades')

const activeTab       = ref(defaultTab.value)
const prioBadge       = ref(0)
const foraBadge       = ref(0)
const aguardandoBadge = ref(0)
const slaBadge        = ref(0)

// If the active tab gets revoked, jump to the first allowed one
watch(canViewAgents, () => {
  if (activeTab.value === 'agentes' && !canViewAgents.value) activeTab.value = defaultTab.value
})

// Auto-foca na aba "Fora de Horário" quando os dados carregam e há conversas com essa etiqueta
watch(
  () => visibleConversations.value.length,
  (newLen, oldLen) => {
    if (oldLen === 0 && newLen > 0) {
      const hasForaLabel = visibleConversations.value.some(c =>
        c.labels.some(lbl => lbl.toLowerCase().replace(/[\s_]+/g, '-') === 'atendimento-fora-de-horario')
      )
      if (hasForaLabel) activeTab.value = 'fora-de-horario'
    }
  }
)

function onShowPopup(data: PopupData, el: HTMLElement) {
  emit('showPopup', data, el)
}
</script>
